import { useState } from 'react';
import { Link } from 'react-router-dom';
import { TICKET_SEVERITY_LABEL, TICKET_STATUS_LABEL, type TicketSeverity, type TicketStatus } from '../../lib/api/testing';
import {
  ticketPhase,
  type FremdTicketDto,
  type StoryTicketDto,
  type TicketPhase,
} from '../../lib/api/abnahme';

const PHASE_TITEL: Record<TicketPhase, string> = {
  offen: 'Offen',
  nachtest: 'Im Nachtest',
  behoben: 'Behoben — wartet auf „erledigt“',
  backlog: 'Zurückgestellt (Backlog)',
  abgeschlossen: 'Abgeschlossen',
};

// Der Backlog steht vor „abgeschlossen" und hinter allem, woran gearbeitet wird:
// Es ist offen im Sinne von „nicht erledigt", aber nichts, worauf jemand wartet.
const REIHENFOLGE: TicketPhase[] = ['offen', 'nachtest', 'behoben', 'backlog', 'abgeschlossen'];

interface Eintrag {
  key: string;
  label: string;
  title: string;
  status: string;
  severity: string;
  id?: number;
  testsystem: boolean;
}

/**
 * Tickets einer Story, nach Phase gruppiert: Nur „Offen“ ist Arbeit, die noch
 * aussteht. Im Nachtest ist der Fix ausgerollt, „behoben“ wartet nur noch auf
 * „erledigt“ — beides bleibt sichtbar, aber nachrangig. Abgeschlossenes ist
 * eingeklappt.
 */
export default function StoryTicketListe({
  tickets,
  fremdTickets,
}: {
  tickets: StoryTicketDto[];
  fremdTickets: FremdTicketDto[];
}) {
  const [abgeschlossenZeigen, setAbgeschlossenZeigen] = useState(false);
  const eintraege: Eintrag[] = [
    ...tickets.map((ticket) => ({
      key: `lokal-${ticket.id}`,
      label: ticket.label,
      title: ticket.title,
      status: ticket.status,
      severity: ticket.severity,
      id: ticket.id,
      testsystem: false,
    })),
    ...fremdTickets.map((ticket) => ({
      key: `${ticket.source}-${ticket.label}`,
      label: ticket.label,
      title: ticket.title,
      status: ticket.status,
      severity: ticket.severity,
      testsystem: true,
    })),
  ];
  if (eintraege.length === 0) {
    return <p className="text-sm text-muted-foreground">Keine Tickets.</p>;
  }
  // Dieselbe Nummer aus beiden Quellen (Testsystem meldet an sich selbst) nur einmal.
  const gesehen = new Set<string>();
  const eindeutig = eintraege.filter((eintrag) => {
    if (gesehen.has(eintrag.label)) return false;
    gesehen.add(eintrag.label);
    return true;
  });

  return (
    <div className="space-y-4">
      {REIHENFOLGE.map((phase) => {
        const liste = eindeutig.filter((eintrag) => ticketPhase(eintrag.status) === phase);
        if (liste.length === 0) return null;
        const eingeklappt = phase === 'abgeschlossen' && !abgeschlossenZeigen;
        return (
          <div key={phase} className="space-y-1.5">
            <button
              type="button"
              className="text-xs font-medium text-muted-foreground"
              onClick={() => phase === 'abgeschlossen' && setAbgeschlossenZeigen(!abgeschlossenZeigen)}
            >
              {PHASE_TITEL[phase]} ({liste.length}){phase === 'abgeschlossen' ? (eingeklappt ? ' ▸' : ' ▾') : ''}
            </button>
            {!eingeklappt && (
              <ul className={`space-y-2 text-sm ${phase === 'offen' ? '' : 'opacity-70'}`}>
                {liste.map((eintrag) => (
                  <li key={eintrag.key}>
                    {eintrag.id ? (
                      <Link to={`/testing/${eintrag.id}`} className="font-mono text-xs underline">
                        {eintrag.label}
                      </Link>
                    ) : (
                      <span className="font-mono text-xs">{eintrag.label}</span>
                    )}{' '}
                    {eintrag.title}
                    <span className="block text-xs text-muted-foreground">
                      {TICKET_STATUS_LABEL[eintrag.status as TicketStatus] ?? eintrag.status} ·{' '}
                      <span className={eintrag.severity === 'blocker' && phase === 'offen' ? 'font-medium text-red-600' : undefined}>
                        {TICKET_SEVERITY_LABEL[eintrag.severity as TicketSeverity] ?? eintrag.severity}
                      </span>
                      {eintrag.testsystem ? ' · Testsystem' : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
